import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';
import { alertarErro } from '../helpers/alerts';

const STATUS_LABEL = {
  disconnected: 'Desconectado',
  qrcode: 'Aguardando QR',
  connecting: 'Conectando...',
  connected: 'Conectado',
  banned: 'Banido',
  error: 'Erro',
};

const STATUS_COLOR = {
  disconnected: '#5E7A82',
  qrcode: '#F5A623',
  connecting: '#F5A623',
  connected: '#4CB553',
  banned: '#b43737',
  error: '#b43737',
};

function StatusBadge({ status }) {
  const cor = STATUS_COLOR[status] || '#5E7A82';
  return (
    <span
      className="badge"
      style={{
        background: `${cor}22`,
        color: cor,
        border: `1px solid ${cor}55`,
        fontWeight: 600,
        padding: '4px 10px',
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function formatNumero(numero) {
  if (!numero) return '—';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  return numero;
}

export default function AdminWhatsapp() {
  const { temPermissao } = useAuth();
  const [canais, setCanais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { canalId, base64, pairing, status, atualizadoEm }
  const [fluxosDisp, setFluxosDisp] = useState([]); // fluxos publicados atribuiveis
  const [setores, setSetores] = useState([]); // filas (fio_setor) p/ setor padrão
  const [saudacaoDraft, setSaudacaoDraft] = useState({}); // rascunho { [canalId]: {texto, ativa} }
  const [bvDraft, setBvDraft] = useState({}); // boas-vindas { [canalId]: {texto, ativa} }
  const pollRef = useRef(null);

  const podeAdmin = temPermissao('admin:whatsapp');
  // Gerente da loja: pode conectar/desconectar/parear, mas NAO criar/excluir/fluxo.
  const podeConectar = podeAdmin || temPermissao('canal:conectar');

  const carregar = async () => {
    try {
      const [res, resF, resS] = await Promise.all([
        axios.get(`${API}/canais`),
        axios.get(`${API}/canais/fluxos-disponiveis`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/setores?ativos=true`).catch(() => ({ data: { data: [] } })),
      ]);
      setCanais(res.data?.data || []);
      setFluxosDisp(resF.data?.data || []);
      setSetores(resS.data?.data || []);
      setErro('');
    } catch (err) {
      setErro('Não foi possível carregar os canais.');
    } finally {
      setLoading(false);
    }
  };

  const atribuirFluxo = async (canal, versaoId) => {
    setSalvando(true);
    try {
      await axios.put(`${API}/canais/${canal.id}/fluxo-versao`, {
        fluxo_versao_id: versaoId || null,
      });
      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra trocar o bot');
    } finally {
      setSalvando(false);
    }
  };

  const atribuirSetorPadrao = async (canal, setorId) => {
    setSalvando(true);
    try {
      await axios.put(`${API}/canais/${canal.id}/setor-padrao`, {
        setor_padrao_id: setorId || null,
      });
      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra trocar o bot');
    } finally {
      setSalvando(false);
    }
  };

  // Saudação ao assumir (por canal). Rascunho local até salvar.
  const getBv = (canal) => bvDraft[canal.id]
    || { texto: canal.msg_boas_vindas || '', ativa: !!canal.msg_boas_vindas_ativa };
  const setBv = (canal, patch) => setBvDraft((d) => {
    const cur = d[canal.id] || { texto: canal.msg_boas_vindas || '', ativa: !!canal.msg_boas_vindas_ativa };
    return { ...d, [canal.id]: { ...cur, ...patch } };
  });
  const salvarBoasVindas = async (canal) => {
    const b = getBv(canal);
    setSalvando(true);
    try {
      await axios.put(`${API}/canais/${canal.id}/boas-vindas`, {
        msg_boas_vindas: b.texto,
        msg_boas_vindas_ativa: b.ativa,
      });
      setBvDraft((d) => { const n = { ...d }; delete n[canal.id]; return n; });
      await carregar();
      Swal.fire({ icon: 'success', title: 'Boas-vindas salva', timer: 1100, showConfirmButton: false });
    } catch (e) {
      alertarErro(e, 'Não deu pra salvar');
    } finally { setSalvando(false); }
  };

  const getSaud = (canal) => saudacaoDraft[canal.id]
    || { texto: canal.saudacao_atendente || '', ativa: !!canal.saudacao_atendente_ativa };
  const setSaud = (canal, patch) => setSaudacaoDraft((d) => {
    const cur = d[canal.id] || { texto: canal.saudacao_atendente || '', ativa: !!canal.saudacao_atendente_ativa };
    return { ...d, [canal.id]: { ...cur, ...patch } };
  });
  const salvarSaudacao = async (canal) => {
    const s = getSaud(canal);
    setSalvando(true);
    try {
      await axios.put(`${API}/canais/${canal.id}/saudacao-atendente`, {
        saudacao_atendente: s.texto,
        saudacao_atendente_ativa: s.ativa,
      });
      await carregar();
      setSaudacaoDraft((d) => { const n = { ...d }; delete n[canal.id]; return n; });
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    } finally {
      setSalvando(false);
    }
  };

  useEffect(() => {
    if (!podeConectar) {
      setLoading(false);
      return undefined;
    }
    carregar();
  }, [podeConectar]);

  // Polling enquanto houver canal em qrcode/connecting OU modal de QR aberto
  const precisaPolling = useMemo(
    () => qrModal !== null || canais.some((c) => c.status === 'qrcode' || c.status === 'connecting'),
    [canais, qrModal],
  );

  useEffect(() => {
    if (!precisaPolling) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(async () => {
      // Sincroniza o status direto do Evolution (chamada de saida). Assim a
      // deteccao de "conectou" NAO depende do webhook chegar de volta — funciona
      // inclusive no ambiente local, onde o webhook nao alcanca a maquina.
      if (qrModal) {
        try { await axios.post(`${API}/canais/${qrModal.canalId}/refresh`); } catch (_) { /* ignora */ }
      }
      await carregar();
      if (qrModal) {
        try {
          const res = await axios.get(`${API}/canais/${qrModal.canalId}/qrcode`);
          setQrModal((prev) => prev && {
            ...prev,
            base64: res.data?.data?.base64,
            pairing: res.data?.data?.pairing_code,
            status: res.data?.data?.status,
            atualizadoEm: res.data?.data?.atualizado_em,
          });
        } catch (err) {
          // QR pode ter expirado/canal desconectado - so o status do canal vai contar
        }
      }
    }, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [precisaPolling, qrModal?.canalId]);

  // Fecha modal automaticamente quando o canal pareia
  useEffect(() => {
    if (!qrModal) return;
    const canal = canais.find((c) => c.id === qrModal.canalId);
    if (canal?.status === 'connected') {
      setQrModal(null);
      Swal.fire({
        icon: 'success',
        title: 'Número pareado!',
        text: `${canal.nome} está conectado.`,
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, [canais, qrModal]);

  const novoCanal = async () => {
    // Carrega filiais (dbo.FILIAIS) e setores de funcionario (dbo.Grupos) para
    // os seletores. Degrada para lista vazia se o usuario nao tiver permissao.
    const [rf, rg] = await Promise.all([
      axios.get(`${API}/atendimento/filiais`).catch(() => ({ data: { data: [] } })),
      axios.get(`${API}/usuarios/grupos`).catch(() => ({ data: { data: [] } })),
    ]);
    const filiais = rf.data?.data || [];
    const grupos = rg.data?.data || [];
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    const lbl = 'display:block;text-align:left;font-size:.82rem;margin:.6rem 0 .15rem;color:var(--portal-muted)';
    const optFiliais = ['<option value="">— Filial (opcional) —</option>']
      .concat(filiais.map((f) => `<option value="${esc(f.codigo)}">${esc(f.codigo)} — ${esc(f.nome)}</option>`))
      .join('');
    const optGrupos = ['<option value="">— Setor (opcional) —</option>']
      .concat(grupos.map((g) => `<option value="${esc(g.nome)}">${esc(g.nome)}</option>`))
      .join('');

    const { value: form } = await Swal.fire({
      title: 'Novo canal WhatsApp',
      html:
        `<label style="${lbl}">Nome do canal</label>` +
        `<input id="fio-nome" class="swal2-input" placeholder="Caixa Loja 01" style="margin:.15rem 0;width:100%">` +
        `<label style="${lbl}">Filial</label>` +
        `<select id="fio-filial" class="swal2-select" style="margin:.15rem 0;width:100%">${optFiliais}</select>` +
        `<label style="${lbl}">Setor</label>` +
        `<select id="fio-setor" class="swal2-select" style="margin:.15rem 0;width:100%">${optGrupos}</select>`,
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
      cancelButtonColor: '#555',
      focusConfirm: false,
      didOpen: () => { document.getElementById('fio-nome')?.focus(); },
      preConfirm: () => {
        const nome = (document.getElementById('fio-nome')?.value || '').trim();
        if (nome.length < 3) {
          Swal.showValidationMessage('Nome precisa ter pelo menos 3 caracteres.');
          return false;
        }
        return {
          nome,
          filial: document.getElementById('fio-filial')?.value || null,
          setor: document.getElementById('fio-setor')?.value || null,
        };
      },
    });
    if (!form) return;

    setSalvando(true);
    try {
      await axios.post(`${API}/canais`, { nome: form.nome, filial: form.filial, setor: form.setor });
      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra criar o canal');
    } finally {
      setSalvando(false);
    }
  };

  const conectar = async (canal) => {
    setSalvando(true);
    try {
      const res = await axios.post(`${API}/canais/${canal.id}/connect`);
      const atualizado = res.data?.data;

      // Abre o modal ja, mesmo sem QR ainda - o polling preenche assim que o
      // webhook qrcode.updated chegar do Evolution.
      setQrModal({
        canalId: canal.id,
        nome: canal.nome,
        base64: atualizado?.qrcode_base64 || null,
        pairing: atualizado?.qrcode_pairing_code || null,
        status: atualizado?.status || 'qrcode',
        atualizadoEm: atualizado?.qrcode_atualizado_em,
      });

      try {
        const qrRes = await axios.get(`${API}/canais/${canal.id}/qrcode`);
        setQrModal((prev) => prev && {
          ...prev,
          base64: qrRes.data?.data?.base64 || prev.base64,
          pairing: qrRes.data?.data?.pairing_code || prev.pairing,
          status: qrRes.data?.data?.status || prev.status,
          atualizadoEm: qrRes.data?.data?.atualizado_em || prev.atualizadoEm,
        });
      } catch (qrErr) {
        // QR ainda nao gerado - polling vai pegar quando chegar.
      }

      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra conectar o canal');
    } finally {
      setSalvando(false);
    }
  };

  // "Parear de novo": força QR novo quando o celular foi deslogado por fora
  // (WhatsApp > Aparelhos conectados > Desconectar). Nesse caso Conectar
  // sozinho não resolve — a Evolution ainda guarda a credencial velha.
  const reparear = async (canal) => {
    if (canal.status === 'connected') {
      const r = await Swal.fire({
        icon: 'warning',
        title: 'Gerar novo QR?',
        text: `Isto desconecta o aparelho que está em "${canal.nome}" e abre um QR code novo pra parear de novo. Use se o WhatsApp foi deslogado no celular.`,
        showCancelButton: true,
        confirmButtonText: 'Gerar novo QR',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#F5A623',
      });
      if (!r.isConfirmed) return;
    }

    setSalvando(true);
    try {
      const res = await axios.post(`${API}/canais/${canal.id}/reparear`);
      const atualizado = res.data?.data;

      setQrModal({
        canalId: canal.id,
        nome: canal.nome,
        base64: atualizado?.qrcode_base64 || null,
        pairing: atualizado?.qrcode_pairing_code || null,
        status: atualizado?.status || 'qrcode',
        atualizadoEm: atualizado?.qrcode_atualizado_em,
      });

      try {
        const qrRes = await axios.get(`${API}/canais/${canal.id}/qrcode`);
        setQrModal((prev) => prev && {
          ...prev,
          base64: qrRes.data?.data?.base64 || prev.base64,
          pairing: qrRes.data?.data?.pairing_code || prev.pairing,
          status: qrRes.data?.data?.status || prev.status,
          atualizadoEm: qrRes.data?.data?.atualizado_em || prev.atualizadoEm,
        });
      } catch (qrErr) {
        // QR ainda chegando — polling pega.
      }

      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra gerar QR novo');
    } finally {
      setSalvando(false);
    }
  };

  const desconectar = async (canal) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Desconectar canal?',
      text: `O número "${canal.nome}" vai parar de receber mensagens até parear novamente.`,
      showCancelButton: true,
      confirmButtonText: 'Desconectar',
      cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;

    setSalvando(true);
    try {
      await axios.post(`${API}/canais/${canal.id}/disconnect`);
      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra desconectar o canal');
    } finally {
      setSalvando(false);
    }
  };

  const toggleFluxo = async (canal) => {
    setSalvando(true);
    try {
      await axios.patch(`${API}/canais/${canal.id}/fluxo`, { fluxo_ativo: !canal.fluxo_ativo });
      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (canal) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Excluir canal?',
      text: `O canal "${canal.nome}" e a sessão no Evolution serão removidos. Esta ação não pode ser desfeita.`,
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!r.isConfirmed) return;

    setSalvando(true);
    try {
      await axios.delete(`${API}/canais/${canal.id}`);
      await carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra excluir');
    } finally {
      setSalvando(false);
    }
  };

  if (!podeConectar) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Você não tem permissão para gerenciar canais WhatsApp.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="mb-1" style={{ color: 'var(--portal-text)' }}>Canais WhatsApp</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Gerencie os números usados para receber atendimentos. Cada número é um canal
            independente, com a própria fila, agenda e equipe.
          </p>
        </div>
        {podeAdmin && (
          <button
            className="btn"
            onClick={novoCanal}
            disabled={salvando}
            style={{
              background: 'linear-gradient(135deg, #3D8B37 0%, #2E6A29 100%)',
              color: '#fff',
              fontWeight: 600,
              border: 'none',
              padding: '8px 18px',
            }}
          >
            <i className="bi bi-plus-lg me-2" /> Novo canal
          </button>
        )}
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : canais.length === 0 ? (
        <div className="card p-4 text-center">
          <p className="mb-0 text-muted">Nenhum canal cadastrado. Clique em "Novo canal" para começar.</p>
        </div>
      ) : (
        <div className="row g-3">
          {canais.map((canal) => (
            <div key={canal.id} className="col-12 col-md-6 col-lg-4">
              {/* Sem selo de "canal ativo": o Fio é multicanal, todo canal
                  conectado recebe atendimento. Quem está no ar é o status da
                  conexão, logo abaixo. */}
              <div
                className="card h-100 p-3"
                style={{ border: '1px solid var(--portal-border)', position: 'relative' }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-whatsapp" style={{ color: '#25D366', fontSize: '1.4rem' }} />
                  <h5 className="mb-0" style={{ color: 'var(--portal-text)' }}>{canal.nome}</h5>
                </div>
                <div className="mb-2"><StatusBadge status={canal.status} /></div>
                <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                  <div><strong>Número:</strong> {formatNumero(canal.numero)}</div>
                  <div><strong>Instance:</strong> <code>{canal.instance_name}</code></div>
                  {canal.conectado_em && (
                    <div><strong>Conectado em:</strong> {new Date(canal.conectado_em).toLocaleString('pt-BR')}</div>
                  )}
                  {/* Erro só enquanto ele É verdade: canal conectado não mostra
                      falha antiga. O backend já limpa ao reconectar; isto aqui é
                      a segunda trava, pra um resíduo no banco não assustar
                      ninguém de novo. */}
                  {canal.ultimo_erro && canal.status !== 'connected' && (
                    <div className="text-danger"><strong>Erro:</strong> {canal.ultimo_erro}</div>
                  )}
                </div>

                {/* Ligar/desligar o bot é operação DA LOJA: o gerente precisa
                    poder calar o bot na hora em que ele atrapalhar, sem
                    depender da TI. Escolher/editar o fluxo continua só do
                    admin — desligar é reversível num clique, trocar o fluxo
                    muda o atendimento de todo mundo. */}
                {podeConectar && (<>
                {/* A chave só significa algo quando existe um fluxo escolhido:
                    sem fluxo, o bot sai silencioso de qualquer jeito (ligada ou
                    desligada dá no mesmo). Então ela fica travada, dizendo isso,
                    em vez de anunciar "ligado" sem ter bot nenhum. */}
                {(() => {
                  const temFluxo = Boolean(canal.fluxo_versao_id);
                  const ligado = temFluxo && canal.fluxo_ativo !== false;
                  return (
                    <div
                      className="d-flex align-items-center gap-2 mt-3"
                      style={{ fontSize: '0.82rem', borderTop: '1px solid var(--portal-border)', paddingTop: 10 }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFluxo(canal)}
                        disabled={salvando || !temFluxo}
                        style={{
                          width: 38,
                          height: 22,
                          borderRadius: 11,
                          border: 'none',
                          padding: 0,
                          cursor: temFluxo ? 'pointer' : 'not-allowed',
                          background: ligado ? '#3D8B37' : '#666',
                          opacity: temFluxo ? 1 : 0.45,
                          position: 'relative',
                          flexShrink: 0,
                          transition: 'background 0.2s',
                        }}
                        title={!temFluxo
                          ? 'Escolha um fluxo abaixo para poder ligar o bot'
                          : (ligado ? 'Desligar o bot neste canal' : 'Ligar o bot neste canal')}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 3,
                            left: ligado ? 18 : 3,
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: '#fff',
                            transition: 'left 0.2s',
                          }}
                        />
                      </button>
                      {temFluxo ? (
                        <span style={{ color: ligado ? 'var(--portal-text)' : 'var(--portal-muted)' }}>
                          Bot do canal: <strong>{ligado ? 'ligado' : 'desligado'}</strong>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--portal-muted)' }}>
                          <strong>Nenhum bot escolhido</strong> — escolha um fluxo abaixo pra poder ligar.
                        </span>
                      )}
                    </div>
                  );
                })()}

                {podeAdmin && (<>
                <div className="mt-2">
                  <label className="form-label mb-1" style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Fluxo do bot (construtor)
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={canal.fluxo_versao_id || ''}
                    onChange={(e) => atribuirFluxo(canal, e.target.value ? Number(e.target.value) : null)}
                    disabled={salvando}
                    title="Fluxo construído no editor que roda neste canal"
                  >
                    <option value="">Sem bot — cai direto na fila</option>
                    {/* Só os bots desta loja. Um bot encaminha pra fila POR ID, e
                        fila é de um canal só: oferecer o de outra loja aqui é
                        oferecer mandar o cliente pra ser atendido lá. O backend
                        recusa de qualquer jeito; a lista nem chega a tentar. */}
                    {fluxosDisp
                      .filter((f) => !Array.isArray(f.canais_ok) || f.canais_ok.includes(canal.id))
                      .map((f) => (
                        <option key={f.versao_id} value={f.versao_id}>{f.nome} (v{f.versao})</option>
                      ))}
                  </select>
                  <div className="mt-1" style={{ fontSize: '0.74rem' }}>
                    {!canal.fluxo_ativo ? (
                      <span style={{ color: '#e0a800' }}>
                        <i className="bi bi-exclamation-triangle me-1" />
                        Bot desligado — nenhuma mensagem aciona o bot, mesmo com um fluxo escolhido.
                      </span>
                    ) : canal.fluxo_versao_id ? (
                      <span style={{ color: '#46b450' }}>
                        <i className="bi bi-check-circle me-1" />
                        Rodando: {canal.fluxo_nome ? `${canal.fluxo_nome} v${canal.fluxo_versao}` : `fluxo v${canal.fluxo_versao_id}`}
                      </span>
                    ) : (
                      <span className="text-muted">
                        <i className="bi bi-inbox me-1" />
                        Sem bot — as mensagens caem direto na fila
                      </span>
                    )}
                  </div>
                </div>

                {/* Setor padrão: quando não há bot roteando, a conversa nova nasce
                    nesta fila (e a distribuição da fila decide atendente x Pendente). */}
                <div className="mt-2">
                  <label className="form-label mb-1" style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Fila padrão — em qual fila a conversa nova entra
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={canal.setor_padrao_id || ''}
                    onChange={(e) => atribuirSetorPadrao(canal, e.target.value ? Number(e.target.value) : null)}
                    disabled={salvando}
                    title="Fila que recebe as conversas quando o canal não tem bot roteando"
                  >
                    <option value="">Pendente geral (ninguém assume até puxar)</option>
                    {/* Fila pertence ao canal: só as filas deste número entram aqui. */}
                    {setores
                      .filter((s) => s.canal_id == null || Number(s.canal_id) === Number(canal.id))
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.nome}</option>
                      ))}
                  </select>
                  <div className="mt-1" style={{ fontSize: '0.74rem' }}>
                    {canal.setor_padrao_id ? (
                      <span style={{ color: '#46b450' }}>
                        <i className="bi bi-check-circle me-1" />
                        Novas conversas entram na fila <strong>{canal.setor_padrao_nome || `#${canal.setor_padrao_id}`}</strong> e são distribuídas conforme o modo dela.
                      </span>
                    ) : canal.fluxo_versao_id && canal.fluxo_ativo !== false ? (
                      <span className="text-muted">
                        <i className="bi bi-robot me-1" />
                        Sem fila padrão, mas o bot está roteando: quem escolhe a fila é o fluxo.
                        Isto aqui só vale pra conversa que o bot não encaminhar.
                      </span>
                    ) : (
                      // A combinação "sem bot + sem fila padrão" é a que joga tudo
                      // no Pendente calado: a distribuição automática da fila só
                      // age DEPOIS que a conversa entra numa fila.
                      <span style={{
                        display: 'block', marginTop: 4, padding: 8, borderRadius: 6,
                        background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.4)',
                        color: 'var(--portal-text)',
                      }}>
                        <i className="bi bi-exclamation-triangle me-1" style={{ color: '#f0ad4e' }} />
                        <strong>Este canal não entrega conversa pra ninguém.</strong> Sem bot e sem fila
                        padrão, toda conversa nova nasce <strong>sem fila</strong> e fica no Pendente até
                        alguém puxar na mão — mesmo que as filas estejam em distribuição automática
                        (ela só age depois que a conversa entra numa fila).
                      </span>
                    )}
                  </div>
                </div>
                </>)}
                </>)}

                {/* Boas-vindas: a PRIMEIRA resposta, antes de alguém assumir. */}
                <div className="mt-3" style={{ borderTop: '1px solid var(--portal-border)', paddingTop: 12 }}>
                  <label className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '0.82rem', color: 'var(--portal-text)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={getBv(canal).ativa}
                      onChange={(e) => setBv(canal, { ativa: e.target.checked })}
                      disabled={salvando}
                    />
                    Responder automaticamente quando o cliente manda a 1ª mensagem
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={getBv(canal).texto}
                    onChange={(e) => setBv(canal, { texto: e.target.value })}
                    disabled={salvando || !getBv(canal).ativa}
                    maxLength={500}
                    placeholder="Ex.: Olá! Recebemos sua mensagem e já vamos te atender. 😊"
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap gap-2">
                    <small style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
                      Sai uma vez por conversa, só enquanto ninguém assumiu.
                      {canal.fluxo_versao_id && (canal.fluxo_ativo ?? true)
                        ? ' Como este canal tem bot publicado, quem fala primeiro é o bot — esta mensagem fica em espera.'
                        : ' Com bot publicado, o bot assume essa função e esta mensagem não é enviada.'}
                    </small>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => salvarBoasVindas(canal)}
                      disabled={salvando || !bvDraft[canal.id]}
                    >
                      Salvar boas-vindas
                    </button>
                  </div>
                </div>

                {/* Transferência livre entre atendentes: com a flag ligada,
                    "Passar pra colega" oferece qualquer atendente do canal —
                    e não só os da fila da conversa. Vale mesmo sem fila. */}
                <div className="mt-3" style={{ borderTop: '1px solid var(--portal-border)', paddingTop: 12 }}>
                  <label className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '0.82rem', color: 'var(--portal-text)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(canal.transferencia_livre_atendente)}
                      onChange={async (e) => {
                        setSalvando(true);
                        try {
                          await axios.put(`${API}/canais/${canal.id}/transferencia-livre`, {
                            transferencia_livre_atendente: e.target.checked,
                          });
                          await carregar();
                        } catch (err) {
                          alertarErro(err, 'Não deu pra salvar');
                        } finally { setSalvando(false); }
                      }}
                      disabled={salvando}
                    />
                    Permitir passar a conversa pra qualquer atendente deste canal
                  </label>
                  <small style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', display: 'block' }}>
                    Ligado: "Passar pra colega" lista todo mundo que atende esse número, inclusive quando a
                    conversa está sem fila. Desligado: só quem está na mesma fila da conversa (o padrão).
                  </small>
                </div>

                {/* Saudação ao assumir — editável por quem pode conectar (gerente da loja). */}
                <div className="mt-3" style={{ borderTop: '1px solid var(--portal-border)', paddingTop: 12 }}>
                  <label className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '0.82rem', color: 'var(--portal-text)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={getSaud(canal).ativa}
                      onChange={(e) => setSaud(canal, { ativa: e.target.checked })}
                      disabled={salvando}
                    />
                    Enviar saudação automática ao assumir o atendimento
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={getSaud(canal).texto}
                    onChange={(e) => setSaud(canal, { texto: e.target.value })}
                    disabled={salvando || !getSaud(canal).ativa}
                    maxLength={500}
                    placeholder="Ex.: Oi! Sou o(a) {atendente} e vou te ajudar por aqui. 😊"
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap gap-2">
                    <small style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
                      Use <code>{'{atendente}'}</code> para o nome de quem assumir.
                    </small>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => salvarSaudacao(canal)}
                      disabled={salvando || !saudacaoDraft[canal.id]}
                    >
                      Salvar saudação
                    </button>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3 flex-wrap">
                  {canal.status !== 'connected' && (
                    <button
                      className="btn btn-sm"
                      onClick={() => conectar(canal)}
                      disabled={salvando}
                      style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
                    >
                      <i className="bi bi-qr-code me-1" /> Conectar
                    </button>
                  )}
                  {canal.status === 'connected' && (
                    <button
                      className="btn btn-sm btn-outline-warning"
                      onClick={() => desconectar(canal)}
                      disabled={salvando}
                    >
                      <i className="bi bi-power me-1" /> Desconectar
                    </button>
                  )}
                  {/* "Ler QR de novo" quando o aparelho foi deslogado no
                      celular: Conectar sozinho não resolve porque a Evolution
                      ainda acha que a sessão vale. Só aparece se o canal já
                      pareou alguma vez — canal novinho não tem o que repair. */}
                  {canal.conectado_em && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => reparear(canal)}
                      disabled={salvando}
                      title="Força um QR code novo. Use quando o WhatsApp foi deslogado no celular."
                    >
                      <i className="bi bi-arrow-clockwise me-1" /> Ler QR de novo
                    </button>
                  )}
                  {podeAdmin && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => excluir(canal)}
                      disabled={salvando}
                    >
                      <i className="bi bi-trash me-1" /> Excluir
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {qrModal && (
        <div
          className="modal d-block"
          style={{ background: 'rgba(0,0,0,0.7)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
              <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
                <h5 className="modal-title">Parear {qrModal.nome}</h5>
                <button className="btn-close btn-close-white" onClick={() => setQrModal(null)} />
              </div>
              <div className="modal-body text-center">
                {qrModal.base64 ? (
                  <>
                    <img
                      src={qrModal.base64.startsWith('data:') ? qrModal.base64 : `data:image/png;base64,${qrModal.base64}`}
                      alt="QR code"
                      style={{ width: 280, height: 280, background: '#fff', padding: 12, borderRadius: 12 }}
                    />
                    {qrModal.pairing && (
                      <p className="mt-3 mb-1" style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>
                        Ou use o código de pareamento:
                      </p>
                    )}
                    {qrModal.pairing && (
                      <code style={{ fontSize: '1.2rem', letterSpacing: 2 }}>{qrModal.pairing}</code>
                    )}
                    <p className="mt-3 mb-0" style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                      No celular: WhatsApp &gt; Aparelhos conectados &gt; Conectar um aparelho.
                      <br />O QR é atualizado a cada poucos segundos automaticamente.
                    </p>
                  </>
                ) : (
                  <div
                    style={{
                      width: 280, height: 280, margin: '0 auto',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 14,
                      background: 'var(--portal-surface-soft)',
                      border: '2px dashed var(--portal-border)', borderRadius: 12,
                    }}
                  >
                    <div
                      className="spinner-border"
                      style={{ color: 'var(--portal-blue)', width: 46, height: 46, borderWidth: 4 }}
                    />
                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--portal-text)' }}>
                      Gerando o QR code…
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                      Aguarde alguns segundos
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
                <button className="btn btn-outline-secondary" onClick={() => setQrModal(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
